import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import { WarehouseTable } from '../../../shared/table';
import { createColumnFactory, exportToCSV } from '../../../shared/table/utils/tableUtils';
import type { ColumnDefinition, ServerFetchParams, TableDataRow } from '../../../shared/table/types/table';
import { adminDataInspectionService } from '../../../../services/adminDataInspectionService';
import { statusCodesService } from '../../../../services/statusCodesService';
import { formatStatusLabel, pickStatusText } from '../../../common/statusPresentation';
import {
  AdminInspectionColumnDto,
  AdminInspectionTableSchemaDto,
  InspectionColumnConfig,
  InspectionTableConfig,
  LinkToConfig,
} from '../../../../types/adminDataInspection';
import { StatusBadge } from '../../../common/StatusBadge';
import { useTranslation } from '../../../../contexts/I18nProvider';

interface TableInspectorViewProps {
  tableConfig: InspectionTableConfig;
  onNavigateToTable?: (targetTable: string, filterKey: string, filterValue: string) => void;
  initialFilter?: { key: string; value: string };
}

type InspectionRow = TableDataRow & Record<string, any>;

// Eksport henter hele det filtrerte utvalget fra serveren, side for side (API-et gir maks 500 per side)
const EXPORT_PAGE_SIZE = 500;
const MAX_EXPORT_ROWS = 50000;

/**
 * Schema-driven table view.
 *
 * Column model = frontend config (default-visible, with label/link/status overrides)
 *              + every remaining column from the table schema (hidden by default).
 * Status/type columns get a StatusBadge either from the config or from the schema's
 * `statusType` (StatusCodes in master). Row ids are built from the schema's key columns.
 */
export const TableInspectorView: React.FC<TableInspectorViewProps> = ({ tableConfig, onNavigateToTable, initialFilter }) => {
  const { t, language } = useTranslation();
  const [data, setData] = useState<InspectionRow[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [schema, setSchema] = useState<AdminInspectionTableSchemaDto | null>(null);

  const isLoadingRef = useRef(false);
  const latestParamsRef = useRef({ pageNumber: 1, pageSize: 50 });
  const filterDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const schemaRef = useRef<AdminInspectionTableSchemaDto | null>(null);

  const columnFactory = createColumnFactory<InspectionRow>();

  // Load schema (cached in service). Data loads independently — extra columns appear when schema arrives.
  useEffect(() => {
    let cancelled = false;
    adminDataInspectionService.getTableSchema(tableConfig.endpoint)
      .then(s => {
        if (cancelled) return;
        schemaRef.current = s;
        setSchema(s);
      })
      .catch(err => {
        console.error(`Error loading schema for ${tableConfig.tableName}:`, err);
        // Fall back to config-only columns
      });
    return () => { cancelled = true; };
  }, [tableConfig.endpoint, tableConfig.tableName]);

  // Format date string for display
  const formatDate = (value: any): string => {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return String(value);
    return d.toLocaleString('nb-NO', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit'
    });
  };

  // Create a link renderer for navigable columns
  // Uses <a> with href so Ctrl+click natively opens in new tab (auth is sync, no redirect)
  const createLinkRenderer = (linkTo: LinkToConfig) => (value: any) => {
    if (value == null || value === '' || value === 0) return <span className="text-gray-400">-</span>;
    if (!onNavigateToTable) return <span>{String(value)}</span>;
    const href = `/?module=admin-data-inspector&table=${linkTo.targetTable}&filterKey=${linkTo.filterKey}&filterValue=${encodeURIComponent(String(value))}`;
    return (
      <a
        href={href}
        onClick={(e) => {
          if (!e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            e.stopPropagation();
            onNavigateToTable(linkTo.targetTable, linkTo.filterKey, String(value));
          }
        }}
        className="text-indigo-600 hover:text-indigo-800 hover:underline font-medium"
        title={t('manager.modules.adminDataInspector.actions.viewIn', { '0': linkTo.targetTable }, `Vis i ${linkTo.targetTable}`)}
      >
        {String(value)}
      </a>
    );
  };

  const createStatusRenderer = (statusType: number, customLabels?: Record<number, string>) => (value: any) => {
    if (value == null) return <span className="text-gray-400">-</span>;
    return <StatusBadge value={Number(value)} statusType={statusType} customLabels={customLabels} />;
  };

  // ------------------------------------------------------------
  // Column builders
  // ------------------------------------------------------------

  /** Pre-configured column (visible by default). `schemaCol` supplies exact key + status fallback when available. */
  const buildConfiguredColumn = (col: InspectionColumnConfig, schemaCol?: AdminInspectionColumnDto): ColumnDefinition<InspectionRow> => {
    // Use the schema's exact camelCase key so row[key] and server filters always line up
    const key = schemaCol?.key ?? col.key;
    const sortable = col.sortable !== false && (schemaCol?.sortable ?? true);
    const statusType = col.statusConfig?.statusType ?? schemaCol?.statusType;

    // Status columns with badge renderer
    if (statusType != null) {
      return {
        ...columnFactory.createNumberColumn(key, key, col.label, {
          width: col.width || 120,
          sortable,
          filterType: (col.filterType ?? 'text') as any,
        }),
        renderer: createStatusRenderer(statusType, col.statusConfig?.customLabels),
      };
    }

    // Date columns
    if (col.type === 'date' || (col.type == null && schemaCol?.category === 'date')) {
      return {
        ...columnFactory.createDateColumn(key, key, col.label, {
          width: col.width || 140,
          sortable,
          filterType: (col.filterType || 'date') as any,
        }),
        formatter: formatDate,
      };
    }

    // Number columns: ID/reference fields (linkTo or filterType=text) use text column to avoid thousand separators
    if (col.type === 'number') {
      const isIdField = !!(col.linkTo || col.filterType === 'text');
      if (isIdField) {
        const colDef = columnFactory.createTextColumn(key, key, col.label, {
          width: col.width || 80,
          sortable,
          filterType: col.filterType as any,
          alignment: 'right',
        });
        return col.linkTo ? { ...colDef, renderer: createLinkRenderer(col.linkTo) } : colDef;
      }
      return columnFactory.createNumberColumn(key, key, col.label, {
        width: col.width || 80,
        sortable,
        filterType: col.filterType as any,
      });
    }

    // Default: text column with optional link
    const colDef = columnFactory.createTextColumn(key, key, col.label, {
      width: col.width || 120,
      sortable,
      filterType: col.filterType as any,
    });
    return col.linkTo ? { ...colDef, renderer: createLinkRenderer(col.linkTo) } : colDef;
  };

  /** Column that exists only in the schema — hidden by default, typed from SQL type, badge if status lookup. */
  const buildSchemaColumn = (c: AdminInspectionColumnDto): ColumnDefinition<InspectionRow> => {
    const base = { visible: false, sortable: c.sortable };

    if (c.statusType != null) {
      return {
        ...columnFactory.createNumberColumn(c.key, c.key, c.name, { ...base, width: 130, filterType: 'text' }),
        renderer: createStatusRenderer(c.statusType),
      };
    }

    switch (c.category) {
      case 'date':
        return {
          ...columnFactory.createDateColumn(c.key, c.key, c.name, { ...base, width: 140, filterType: 'date' }),
          formatter: formatDate,
        };
      case 'boolean':
        return columnFactory.createBooleanColumn(c.key, c.key, c.name, { ...base, width: 80, filterType: 'text' });
      case 'number':
        // Text column with right alignment: no thousand separators on IDs/reference numbers
        return columnFactory.createTextColumn(c.key, c.key, c.name, { ...base, width: 100, filterType: 'text', alignment: 'right' });
      default:
        return columnFactory.createTextColumn(c.key, c.key, c.name, { ...base, width: 140, filterType: 'text' });
    }
  };

  // Build WarehouseTable columns: config (visible) + remaining schema columns (hidden)
  const columns: ColumnDefinition<InspectionRow>[] = useMemo(() => {
    const schemaByKey = new Map<string, AdminInspectionColumnDto>();
    schema?.columns.forEach(c => schemaByKey.set(c.key.toLowerCase(), c));

    const configured = tableConfig.columns.map(col => {
      const schemaCol = schemaByKey.get(col.key.toLowerCase());
      return buildConfiguredColumn(col, schemaCol);
    });

    const configuredKeys = new Set(configured.map(c => String(c.key).toLowerCase()));
    const extras = (schema?.columns ?? [])
      .filter(c => !configuredKeys.has(c.key.toLowerCase()))
      .map(buildSchemaColumn);

    return [...configured, ...extras];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableConfig, schema, columnFactory, onNavigateToTable, t]);

  // Statuskolonner (samme oppslag som i kolonnebyggerne) — eksporten skriver «60 - Eksportert», ikke bare koden
  const statusColumns = useMemo(() => {
    const map = new Map<string, { statusType: number; customLabels?: Record<number, string> }>();
    const schemaByKey = new Map<string, AdminInspectionColumnDto>();
    schema?.columns.forEach(c => schemaByKey.set(c.key.toLowerCase(), c));

    tableConfig.columns.forEach(col => {
      const schemaCol = schemaByKey.get(col.key.toLowerCase());
      const statusType = col.statusConfig?.statusType ?? schemaCol?.statusType;
      if (statusType != null) {
        map.set(schemaCol?.key ?? col.key, { statusType, customLabels: col.statusConfig?.customLabels });
      }
    });
    schema?.columns.forEach(c => {
      if (c.statusType != null && !map.has(c.key)) map.set(c.key, { statusType: c.statusType });
    });
    return map;
  }, [tableConfig, schema]);

  // Stable row id from the table's key columns (CompanyNo excluded server-side)
  const buildRowId = (item: Record<string, any>, index: number): string | number => {
    const keys = schemaRef.current?.keyColumns;
    if (keys && keys.length > 0) {
      const parts = keys.map(k => item[k]);
      if (parts.every(p => p != null && p !== '')) {
        return parts.length === 1 ? parts[0] : parts.join('|');
      }
    }
    return item.id ?? item.pListID ?? item.batchID ?? index;
  };

  // Fetch data from server
  const fetchData = useCallback(async (params: { pageNumber: number; pageSize: number; sortColumn?: string; sortDirection?: string; filters?: Record<string, string> }) => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const result = await adminDataInspectionService.getPagedTable<Record<string, any>>(
        tableConfig.endpoint,
        params
      );

      const rows: InspectionRow[] = result.items.map((item, index) => ({
        ...item,
        id: buildRowId(item, index),
      }));

      setData(rows);
      setTotalRecords(result.totalRecords);
      latestParamsRef.current = params;
    } catch (err) {
      console.error(`Error loading ${tableConfig.tableName}:`, err);
      setError(t('manager.modules.adminDataInspector.errors.loadFailed', {}, 'Kunne ikke laste data. Prøv igjen.'));
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableConfig.endpoint, tableConfig.tableName, t]);

  // Server-side pagination callback
  const handleServerFetch = useCallback((params: ServerFetchParams) => {
    const queryParams = {
      pageNumber: params.pageNumber,
      pageSize: params.pageSize,
      sortColumn: params.sortColumn,
      sortDirection: params.sortDirection,
      filters: params.filters,
    };

    if (filterDebounceRef.current) {
      clearTimeout(filterDebounceRef.current);
    }

    const prevFilters = (latestParamsRef.current as any).filters;
    const isFilterChange = params.pageNumber === 1 && JSON.stringify(params.filters) !== JSON.stringify(prevFilters);

    if (isFilterChange) {
      filterDebounceRef.current = setTimeout(() => {
        fetchData(queryParams);
      }, 300);
    } else {
      fetchData(queryParams);
    }
  }, [fetchData]);

  const refreshData = useCallback(() => {
    fetchData(latestParamsRef.current as any);
  }, [fetchData]);

  // Initial load (with optional pre-applied filter from cross-table navigation)
  useEffect(() => {
    const params: any = {
      pageNumber: 1,
      pageSize: 50,
      sortColumn: tableConfig.defaultSort,
      sortDirection: 'desc'
    };
    if (initialFilter) {
      params.filters = { [initialFilter.key]: initialFilter.value };
    }
    fetchData(params);
  }, [fetchData, tableConfig.defaultSort, initialFilter]);

  // Cleanup debounce
  useEffect(() => {
    return () => {
      if (filterDebounceRef.current) {
        clearTimeout(filterDebounceRef.current);
      }
    };
  }, []);

  // Eksporter: alle rader som matcher gjeldende filter og sortering, med kolonnene som er synlige i tabellen
  const handleExport = useCallback(async (_pageData: InspectionRow[], visibleColumns: ColumnDefinition<InspectionRow>[]) => {
    if (isExporting) return;
    setExportError(null);

    if (totalRecords > MAX_EXPORT_ROWS) {
      setExportError(t(
        'manager.modules.adminDataInspector.export.tooManyRows',
        { '0': totalRecords.toLocaleString('nb-NO'), '1': MAX_EXPORT_ROWS.toLocaleString('nb-NO') },
        `Utvalget har ${totalRecords.toLocaleString('nb-NO')} rader. Eksporten tar maks ${MAX_EXPORT_ROWS.toLocaleString('nb-NO')} — snevre inn filteret.`
      ));
      return;
    }

    setIsExporting(true);
    try {
      const { sortColumn, sortDirection, filters } = latestParamsRef.current as any;
      const rows: Record<string, any>[] = [];
      for (let pageNumber = 1; ; pageNumber++) {
        const result = await adminDataInspectionService.getPagedTable<Record<string, any>>(tableConfig.endpoint, {
          pageNumber, pageSize: EXPORT_PAGE_SIZE, sortColumn, sortDirection, filters,
        });
        rows.push(...result.items);
        if (result.items.length < EXPORT_PAGE_SIZE || rows.length >= result.totalRecords || rows.length >= MAX_EXPORT_ROWS) break;
      }

      // Statuskoder → «kode - tekst» på aktivt språk
      const labelsByKey = new Map<string, Map<number, string>>();
      for (const col of visibleColumns) {
        const key = String(col.key);
        const status = statusColumns.get(key);
        if (!status) continue;
        const labels = new Map<number, string>();
        if (status.customLabels) {
          Object.entries(status.customLabels).forEach(([code, text]) => labels.set(Number(code), formatStatusLabel(Number(code), text)));
        } else {
          const codes = await statusCodesService.getByStatusType(status.statusType).catch(() => []);
          codes.forEach(c => labels.set(c.statusCode, formatStatusLabel(c.statusCode, pickStatusText(c, language))));
        }
        labelsByKey.set(key, labels);
      }

      const exportRows: InspectionRow[] = rows.map((item, index) => {
        const row: InspectionRow = { ...item, id: index };
        labelsByKey.forEach((labels, key) => {
          const value = row[key];
          if (value != null) row[key] = labels.get(Number(value)) ?? value;
        });
        return row;
      });

      exportToCSV(exportRows, visibleColumns, `${tableConfig.tableName}-${new Date().toISOString().split('T')[0]}.csv`);
    } catch (err) {
      console.error(`Error exporting ${tableConfig.tableName}:`, err);
      setExportError(t('manager.modules.adminDataInspector.export.failed', {}, 'Eksporten feilet. Prøv igjen.'));
    } finally {
      setIsExporting(false);
    }
  }, [isExporting, totalRecords, tableConfig.endpoint, tableConfig.tableName, statusColumns, language, t]);

  const tableActions = useMemo(() => [
    {
      id: 'refresh',
      label: t('common.buttons.refresh', {}, 'Oppdater'),
      icon: RefreshCw,
      onClick: () => refreshData(),
      variant: 'secondary' as const
    }
  ], [refreshData, t]);

  return (
    <div className="flex-1 min-h-0">
      <WarehouseTable
        data={data}
        columns={columns}
        config={{
          showPagination: true,
          showColumnConfig: true,
          showGlobalSearch: false,
          showFilters: true,
          showExport: true,
          showSummary: false,
          defaultPageSize: 50,
          allowColumnReorder: true,
          allowColumnResize: true,
          stickyHeader: true,
          compactMode: true,
          persistenceKey: `admin-inspect-${tableConfig.tableName}`
        }}
        serverPagination={{
          enabled: true,
          totalRecords: totalRecords,
          onFetchData: handleServerFetch
        }}
        tableActions={tableActions}
        actions={{ onExport: handleExport }}
        toolbarRight={(isExporting || exportError) && (
          <span className={`text-xs flex items-center gap-1.5 ${exportError ? 'text-red-600' : 'text-blue-700'}`}>
            {isExporting && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
            {exportError ?? t('manager.modules.adminDataInspector.export.inProgress', {}, 'Eksporterer...')}
          </span>
        )}
        loading={isLoading}
        error={error || undefined}
      />
    </div>
  );
};
